import type { ReactNode } from 'react';
import type { AgentPageStatus } from '@erp/rpc';
import type { RouterNavEntry } from '@/routing/nav-from-router';
import type { TaskPageNavItem } from '@/routing/task-pages-nav';

/**
 * Pure nav helpers for org-configurable agent pages in the sidebar. Kept free
 * of React/query imports so it stays trivially unit-testable.
 *
 * Agent pages ride the task-page sidebar merge (`mergeTaskPagesIntoNav`): an
 * agent page nav item has the same shape, so both kinds of page are merged in
 * one pass and share the generic "Pages" fallback section.
 */
export type AgentPageNavItem = TaskPageNavItem;

/**
 * What the sidebar shows for a page's agent:
 *  - `running` — the agent's latest run is in progress.
 *  - `failed`  — the latest run ended in failure and nothing has run since.
 *  - `null`    — idle after a completed run, never run, or status not loaded.
 */
export type AgentPageBadgeState = { kind: 'running' } | { kind: 'failed' };

export function agentPageBadgeState(
  status: Pick<AgentPageStatus, 'latestRunStatus'> | undefined,
): AgentPageBadgeState | null {
  if (!status) return null;
  if (status.latestRunStatus === 'running') return { kind: 'running' };
  if (status.latestRunStatus === 'failed') return { kind: 'failed' };
  return null;
}

/**
 * Nav entry for one agent page. `icon` and `badge` are prebuilt nodes so this
 * stays JSX-free.
 */
export function agentPageNavEntry({
  page,
  icon,
  badge = null,
}: {
  page: { id: string; name: string };
  icon: ReactNode;
  badge?: ReactNode;
}): RouterNavEntry {
  return {
    id: `agent-page:${page.id}`,
    fullPath: `/agent-pages/${page.id}`,
    meta: { nav: { label: page.name, icon, badge } },
  };
}
